'use client';

import { PortableText } from '@portabletext/react';
import { stegaClean } from '@sanity/client/stega';
import { cn } from '@/lib/utils';
import { GroqImage } from '@/helpers/Image';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PageTextOnlyCryptoSchema } from '@/sanity/pages/PageTextOnlyCryptoTemplate';
import { DotTexture } from './textures';

/** Figma: desktop 7218:78, mobile 8662:24517. */
export default function PayoutSection({
  data,
  className,
}: {
  data: PageTextOnlyCryptoSchema;
  className?: string;
}) {
  // Use the detailed Figma crop only for this asset; CMS replacements and clearing still take effect.
  const hasMobileCrop = stegaClean(data?.payoutImage?.src ?? '').endsWith(
    '/3a5edd1d6296b188b9c896969c1da2da75ed3036-756x356.png',
  );

  return (
    <section
      id="chapter-1"
      data-chapter
      className={cn(
        'relative flex justify-center w-full bg-white px-[2rem] lg:px-0 pt-[8rem] lg:pt-[12.5rem]',
        className,
      )}
    >
      <div className="relative flex flex-col gap-[1rem] w-[min(94.5rem,100%)] overflow-clip rounded-[1rem] border border-black/10 bg-white">
        <DotTexture className="left-[36.369rem] top-[37.75rem]" />

        {data?.payoutImage && (
          <div className="relative w-full max-lg:aspect-[398/355] max-lg:max-h-[min(44.375rem,355px)] lg:h-[44.39rem] shrink-0">
            <picture>
              {hasMobileCrop && (
                <source
                  media="(max-width: 639px)"
                  srcSet="/text-only-crypto/payout-mobile.webp"
                />
              )}
              <GroqImage
                src={data.payoutImage}
                alt=""
                quality={100}
                className={cn(
                  'absolute inset-0 !w-full !h-full object-cover',
                  hasMobileCrop && 'max-sm:object-top',
                )}
                style={{ width: '100%', height: '100%' }}
              />
            </picture>
          </div>
        )}

        <div className="relative flex flex-col gap-[1rem] w-full max-lg:p-[2rem] lg:p-[5.25rem]">
          {data?.payoutChip && (
            <div className="flex items-center h-[3.375rem]">
              <p className="mb-0 font-blacker italic text-[2rem] leading-[1.12] tracking-[0.02rem] text-[#103037] whitespace-nowrap">
                {data.payoutChip}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-[2.5rem] w-full">
            {data?.payoutText && (
              <div
                className="richtext richtext-start w-full
                prose-p:font-geist prose-p:font-medium max-lg:prose-p:text-[3.5rem] lg:prose-p:text-[4rem] prose-p:leading-[1.24] max-lg:prose-p:tracking-[-0.105rem] lg:prose-p:tracking-[-0.12rem] prose-p:text-black prose-p:mb-[2rem] max-lg:[&>p:first-child]:max-w-[37.375rem] max-lg:[&>p:first-child]:[text-wrap:wrap] lg:prose-p:max-w-[83.375rem]
                [&>p:not(:first-child)]:!font-normal [&>p:not(:first-child)]:!text-[2.25rem] [&>p:not(:first-child)]:!leading-[1.4] [&>p:not(:first-child)]:!tracking-[-0.0675rem] [&>p:not(:first-child)]:opacity-80 lg:[&>p:not(:first-child)]:!max-w-[52.5rem]"
              >
                <PortableText
                  value={data.payoutText}
                  components={richTextComponent}
                />
              </div>
            )}

            {data?.payoutNote && (
              <p className="mb-0 font-geist text-[2.25rem] leading-[1.4] tracking-[-0.0675rem] text-black opacity-80 max-lg:w-[min(39rem,100%)] lg:w-[52.5rem] whitespace-pre-wrap">
                {data.payoutNote}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
